import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useNavigateWithParams } from "../../../hooks/useNavigateWithParams";
import { AppRoutes } from "../../../routes/AppRoutes";
import { useEditSubscriptionMutation, useAcceptRetentionOfferMutation } from "../../../rtk/currentSubscriptionsOptions/currentSubscriptionsOptions";
import { RetentionOffer } from "../../../types/rtk/currentSubscriptionsOptions/currentSubscriptionsOptions";

import LogoSvg from "../../atoms/LogoSvg/LogoSvg";
import Title from "../../atoms/Title/Title";
import Text from "../../atoms/Text/Text";
import Footer from "../../templates/Footer/Footer";
import Header from "../../templates/Header/Header";
import StyledButton from "../../atoms/StyledButton/StyledButton";

import FAIL from "../../../assets/images/FAIL.svg";
import BALLOON from "../../../assets/images/BALLOON.svg";
import HELP_BIG from "../../../assets/images/HELP_BIG.svg";

import { getSubscriptionTitle } from "../../../helpers/getSubscriptionTitle";
import { formatPrice } from "../../../helpers/currencyHelper";
import { formatDate } from "../../../helpers/formatDate";

const ManageSubscriptionsRetentionOffers = () => {
  const { t } = useTranslation();
  const navigate = useNavigateWithParams();
  const location = useLocation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [manageSubscriptionReason, { isLoading: isLoadingCancel }] = useEditSubscriptionMutation();
  const [acceptRetentionOffer, { isLoading: isLoadingAccept }] = useAcceptRetentionOfferMutation();
  const isLoading = isLoadingCancel || isLoadingAccept;

  const offers: RetentionOffer[] = location.state?.offers ?? [];
  const cancelData: { cancel_reason: string; cancel_explanation?: string } = location.state?.cancelData;
  const currentOffer = offers[currentIndex];

  const getContent = () => {
    switch (currentOffer?.offer_type) {
      case 'upgrade_promo': {
        return {
          image: BALLOON,
          title: t("subscription:MANAGE_PAGE.RETENTION_OFFERS.UPGRADE.TITLE", {
            discount_ratio: (currentOffer.discount_ratio ?? 0) * 100,
            upgrade_offer_title: currentOffer.upgrade_period != null ? getSubscriptionTitle(currentOffer.upgrade_period) : '',
          }),
          description: t("subscription:MANAGE_PAGE.RETENTION_OFFERS.UPGRADE.DESCRIPTION", {
            tracker_name: location.state?.tracker_name ?? '',
            savings_amount: formatPrice((currentOffer.original_amount! - currentOffer.upgrade_amount!) / 100, currentOffer.upgrade_currency!),
            upgrade_offer_title: currentOffer.upgrade_period != null ? getSubscriptionTitle(currentOffer.upgrade_period) : '',
            upgrade_amount: formatPrice(currentOffer.upgrade_amount! / 100, currentOffer.upgrade_currency!),
            upgrade_amount_monthly: `${formatPrice((currentOffer.upgrade_amount! / currentOffer.upgrade_period!) / 100, currentOffer.upgrade_currency!)} / ${t("subscription:MONTH")}`,
            next_charge_at: formatDate(location.state?.next_charge_at),
          }),
          accept_button: t("subscription:MANAGE_PAGE.RETENTION_OFFERS.UPGRADE.BUTTON", {savings: formatPrice((currentOffer.original_amount! - currentOffer.upgrade_amount!) / 100, currentOffer.upgrade_currency!)}),
        };
      }
      case 'free_months':
        return {
          image: BALLOON,
          title: t("subscription:MANAGE_PAGE.RETENTION_OFFERS.FREE_MONTHS.TITLE", {
            free_months: currentOffer.free_months
          }),
          description: t("subscription:MANAGE_PAGE.RETENTION_OFFERS.FREE_MONTHS.DESCRIPTION", {
            free_months: currentOffer.free_months,
            tracker_name: location.state?.tracker_name ?? '',
            next_charge_at_with_free_months: formatDate((() => {
              const d = new Date(location.state?.next_charge_at);
              d.setMonth(d.getMonth() + (currentOffer.free_months ?? 0));
              return d.toISOString();
            })()),
          }),
        };
      case 'create_ticket':
        return {
          image: HELP_BIG,
          title: t("subscription:MANAGE_PAGE.RETENTION_OFFERS.CREATE_TICKET.TITLE", { offer: currentOffer }),
          description: t("subscription:MANAGE_PAGE.RETENTION_OFFERS.CREATE_TICKET.DESCRIPTION", { offer: currentOffer }),
          accept_button: t("subscription:MANAGE_PAGE.RETENTION_OFFERS.CREATE_TICKET.BUTTON", { offer: currentOffer })
        };
      default:
        return {
          image: FAIL,
          title: t("subscription:MANAGE_PAGE.RETENTION_OFFERS.DEFAULT.TITLE"),
          description: t("subscription:MANAGE_PAGE.RETENTION_OFFERS.DEFAULT.DESCRIPTION"),
        };
    }
  };

  const { image, title, description, accept_button } = getContent();

  const handleGoBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      navigate(AppRoutes.MANAGE_SUBSCRIPTIONS_CANCEL_REASON, { state: location.state });
    }
  };

  const handleAcceptOffer = async () => {
    try {
      await acceptRetentionOffer({
        retention_offer_id: currentOffer.id,
        cancel_explanation: cancelData?.cancel_explanation,
      }).unwrap();
      navigate(AppRoutes.MANAGE_SUBSCRIPTIONS_EDIT_RESULT, { state: { context: 'offer_success', offer: currentOffer, next_charge_at: location.state?.next_charge_at, tracker_name: location.state?.tracker_name } });
    } catch {
      navigate(AppRoutes.MANAGE_SUBSCRIPTIONS_EDIT_RESULT, { state: { context: 'error' } });
    }
  };

  const handleContinueCancellation = async () => {
    if (currentIndex < offers.length - 1) {
      setCurrentIndex(currentIndex + 1);
      return;
    }
    try {
      await manageSubscriptionReason({
        renewal_active: false,
        ...cancelData
      }).unwrap();
      navigate(AppRoutes.MANAGE_SUBSCRIPTIONS_EDIT_RESULT, { state: { context: 'cancellation_success', type: 'subscription' } });
    } catch {
      navigate(AppRoutes.MANAGE_SUBSCRIPTIONS_EDIT_RESULT, { state: { context: 'error' } });
    }
  };

  return (
    <>
      <Header
        headerStep={{ context: "manage" }}
        title={t("subscription:MANAGE_PAGE.EDIT_SUBSCRIPTION.HEADER")}
        goBack={handleGoBack}
      />
      <Box className="ManageSubscriptionsRetentionOffers">
        <Box className="content">
          <LogoSvg className="Logos" src={image} />
          <Title variant="h1">{title}</Title>
          <Text>{description}</Text>
        </Box>
      </Box>
      <Footer headerStep={{ context: "manage" }}>
        <Box className="buttonContainer">
          <StyledButton onClick={handleAcceptOffer} disabled={isLoading}>
            {accept_button || t("subscription:MANAGE_PAGE.RETENTION_OFFERS.BUTTON.ACCEPT")}
          </StyledButton>
          <StyledButton className="white" onClick={handleContinueCancellation} disabled={isLoading}>
            {t("subscription:MANAGE_PAGE.RETENTION_OFFERS.BUTTON.CONTINUE_CANCELLATION")}
          </StyledButton>
        </Box>
      </Footer>
    </>
  );
};

export default ManageSubscriptionsRetentionOffers;
